/**
 * Job-request attachment API helpers.
 * All calls are authenticated; the backend enforces ownership, country (AU)
 * and matched-provider/admin authorisation. Signed view URLs are short-lived.
 */
import { API_URL } from "./supabase";

export const ATTACH_LIMITS = {
  maxFiles: 10,
  maxCombinedBytes: 100 * 1024 * 1024,
  maxDurationSeconds: 60,
  perType: {
    image: 10 * 1024 * 1024,
    document: 10 * 1024 * 1024,
    video: 50 * 1024 * 1024,
    audio: 15 * 1024 * 1024,
  },
};

export const ACCEPTED = {
  "image/jpeg": "image", "image/png": "image", "image/webp": "image",
  "image/heic": "image", "image/heif": "image",
  "video/mp4": "video", "video/quicktime": "video", "video/x-m4v": "video", "video/webm": "video",
  "audio/mpeg": "audio", "audio/mp4": "audio", "audio/aac": "audio",
  "audio/wav": "audio", "audio/x-wav": "audio", "audio/webm": "audio",
  "application/pdf": "document",
};

export const categoryForType = (mime) => ACCEPTED[(mime || "").split(";")[0].trim().toLowerCase()] || null;

export const humanSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Upload one attachment with progress via XHR. Returns the created row. */
export function uploadAttachment({ file, uploadSessionId, durationSeconds, thumbnailDataUrl, accessToken, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("file", file, file.name || "upload");
    fd.append("upload_session_id", uploadSessionId);
    if (durationSeconds != null) fd.append("duration_seconds", String(durationSeconds));
    if (thumbnailDataUrl) fd.append("thumbnail_data_url", thumbnailDataUrl);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/quote-attachments`);
    xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let body = null;
      try { body = JSON.parse(xhr.responseText); } catch { body = null; }
      if (xhr.status >= 200 && xhr.status < 300 && body) resolve(body);
      else reject(new Error(body?.detail || `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload."));
    xhr.onabort = () => reject(new Error("Upload cancelled."));
    if (signal) signal.addEventListener("abort", () => xhr.abort());
    xhr.send(fd);
  });
}

export async function deleteAttachment(id, accessToken) {
  const res = await fetch(`${API_URL}/quote-attachments/${id}`, {
    method: "DELETE", headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    let b = null; try { b = await res.json(); } catch {}
    throw new Error(b?.detail || "Could not remove attachment.");
  }
  return true;
}

export async function listQuoteAttachments(quoteId, accessToken) {
  const res = await fetch(`${API_URL}/quote-attachments/by-quote/${quoteId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (res.status === 403) return []; // not authorised = nothing to show
  if (!res.ok) throw new Error(`Could not load attachments (${res.status})`);
  try { return await res.json(); } catch { return []; }
}

export async function getViewUrl(id, accessToken, { thumbnail = false } = {}) {
  const res = await fetch(`${API_URL}/quote-attachments/${id}/view-url${thumbnail ? "?thumbnail=true" : ""}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    let b = null; try { b = await res.json(); } catch {}
    throw new Error(b?.detail || "Could not open attachment.");
  }
  return res.json(); // { url, mime_type, category }
}

/** Detect the best supported MediaRecorder mime for a kind ('audio'|'video'). */
export function pickRecorderMime(kind) {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return null;
  const candidates = kind === "audio"
    ? ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac", "audio/mpeg"]
    : ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
  for (const c of candidates) { if (MediaRecorder.isTypeSupported(c)) return c; }
  return null;
}

// True when the browser can record at all. iOS Safari commonly reports NO
// isTypeSupported match yet still records fine with its default container
// (audio/mp4, video/mp4). So we intentionally do NOT require a matched mime
// here — the recorder falls back to the browser default and reads the real
// mime from the MediaRecorder instance after recording. This is what unblocks
// voice-note and video recording on iPhone.
export const recorderSupported = () =>
  typeof navigator !== "undefined" && !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === "function" &&
  typeof MediaRecorder !== "undefined";
